import { Grade, GradeConfig, GradeType, IGradeApiCall, IRestApiService } from '../types';
import { GradeTracker } from '../services/analytics/apiTrackers/gradeTracking';

const gradeTypes: GradeType[] = ['lessonGrade', 'homeworkGrade', 'behavioralGrade'];

const normalizeValue = (value?: string | null): string | null => value?.trim() || null;

export const normalizeGradeEntries = (grades: Grade[], includeEmpty: boolean): Grade[] =>
  gradeTypes
    .map((gradeType) => {
      const entry = grades.find((grade) => grade.gradeType === gradeType);
      return {
        gradeType,
        grade: normalizeValue(entry?.grade),
        comment: normalizeValue(entry?.comment),
      };
    })
    .filter((entry) => includeEmpty || entry.grade !== null || entry.comment !== null);

class GradeApiCall implements IGradeApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getGrade(studioClientId: string, attendanceId: string): Promise<Grade[]> {
    return this.restApiService.get(`grade/${studioClientId}/${attendanceId}`);
  }

  updateGrades(studioClientId: string, attendanceId: string, grades: Grade[], expectedGrades: Grade[]): Promise<void> {
    const startTime = Date.now();

    return this.restApiService
      .patch(`grade/studioClientId/${studioClientId}/attendanceId/${attendanceId}`, {
        grades: normalizeGradeEntries(grades, true),
        expectedGrades: normalizeGradeEntries(expectedGrades, false),
      })
      .then((result) => {
        GradeTracker.trackUpdateGrades({
          studioClientId,
          attendanceId,
          grades,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        GradeTracker.trackUpdateGrades({
          studioClientId,
          attendanceId,
          grades,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addGrades(studioClientId: string, attendanceId: string, grades: Grade[], expectedGrades: Grade[]): Promise<void> {
    const startTime = Date.now();

    return this.restApiService
      .post(`grade/studioClientId/${studioClientId}/attendanceId/${attendanceId}`, {
        grades: normalizeGradeEntries(grades, true),
        expectedGrades: normalizeGradeEntries(expectedGrades, false),
      })
      .then((result) => {
        GradeTracker.trackAddGrades({
          studioClientId,
          attendanceId,
          grades,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        GradeTracker.trackAddGrades({
          studioClientId,
          attendanceId,
          grades,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }
  getGradeConfig(ownerId: string): Promise<GradeConfig> {
    return this.restApiService.get(`grade/config/owner/${ownerId}`);
  }

  addGradeConfig(ownerId: string): Promise<GradeConfig> {
    const startTime = Date.now();

    return this.restApiService
      .post(`grade/config/owner/${ownerId}`)
      .then((result) => {
        GradeTracker.trackAddGradeConfig({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        GradeTracker.trackAddGradeConfig({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }
}

export default GradeApiCall;
